import React, { Component } from "react";
import PropTypes from "prop-types";

import Breadcrumbs from "react-router-dynamic-breadcrumbs";
import SweetAlert from "sweetalert-react";
import i18n from "i18n-react";
import posed, { PoseGroup } from "react-pose";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Redirect, withRouter, Link } from "react-router-dom";

import TrainingContent, { LinkButton, NavigationButton } from "components/TrainingContent";
import { APIHandler, DataHandler, StorageHandler, ProgressHandler, Progress } from "C4";
import { storageKeys, buttonRules } from "settings";

import "assets/styles/sweetalert.css";

const AnimationContainer = posed.section({
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 200
    }
  },
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 200
    }
  }
});

class TrainingContentView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      appId: -1,
      buttonContentCompleted: props.type === "retain",
      buttons: [],
      buttonsCompleted: 0,
      completedBefore: null,
      contentCompleted: props.type === "retain",
      crumbRoutes: {},
      errors: 0,
      pages: {},
      ready: false,
      user: ""
    };
  }

  componentWillMount() {
    const { history, type } = this.props;
    const { chapterId, pageId } = this.props.match.params;
    this.init().then(() => {
      this.initProgress().then(prog => {
        this.progress = new ProgressHandler(prog);
        this.setState({ ready: true });
      });
    });

    StorageHandler.load(storageKeys.user).then(res => {
      if (res.response) {
        this.setState({ user: res.response.email });
      }
    });

    const mappedChapters = {};

    DataHandler.matchAppId().then(appId => {
      DataHandler.checkKey(
        storageKeys.chapters,
        APIHandler.getChapters(appId)
      ).then(chapters => {
        if (pageId === "null") {
          history.push(`${this.props.redirPath}${chapterId}/0`);
        }
        chapters.app.forEach(chapter => {
          mappedChapters[chapter.chapterID] = chapter.chapterTitle;
        });

        const incrementBreadcrumbPage = crumbPageId => +crumbPageId + 1;

        this.setState({
          crumbRoutes: {
            "/main": null,
            [`/main/${type}`]: null,
            [`/main/${type}/chapter`]: null,
            [`/main/${type}/chapter/:crumbChapterId`]: null,
            [`/main/${type}/chapter/:crumbChapterId/:crumbPageId`]: (
              url,
              match
            ) =>
              `${mappedChapters[match[":crumbChapterId"]]} - ${i18n.translate(
                "breadCrumbs.page"
              )} ${incrementBreadcrumbPage(match[":crumbPageId"])}`
          }
        });
      });
    });
  }

  componentDidMount() {
    const d = new Date(Date.now());
    const leading = val => `0${val}`.slice(-2);

    StorageHandler.load(storageKeys.user).then(user => {
      if (user.response && user.response.training_started === null) {
        const obj = user;
        const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(
          d.getDate()
        )}`;
        DataHandler.matchAppId().then(appId => {
          APIHandler.updateUser(appId, obj.response.email, [
            { param: "training_started", val: date }
          ]).then(res => {
            if (res) {
              obj.response.training_started = date;
              StorageHandler.save(storageKeys.user, obj);
            }
          });
        });
      }
    });
  }

  componentDidUpdate() {
    const { pageId, chapterId } = this.props.match.params;
    const { completedBefore } = this.state;
    if (this.progress !== undefined && completedBefore === null) {
      this.checkCompletion(pageId, chapterId);
    }
  }

  setButtons(page) {
    const buttons = [];
    let index = 1;
    let mandatoryAmount = 0;

    if (page !== undefined) {
      Object.keys(page).forEach(() => {
        if (buttonRules[page[`linkType${index}`]]) {
          mandatoryAmount += 1;
        }
        index += 1;
      });

      index = 1;
      Object.keys(page).forEach(link => {
        const mandatory = buttonRules[page[`linkType${index}`]];
        const links = [page[`link${index}`], page[`linkType${index}`]].every(
          val => val !== undefined && val.length > 0
        );
        const linksValid = page.question === undefined && links;
        if (link === `link${index}` && linksValid) {
          buttons.push(
            <LinkButton
              key={page[`linkType${index}`]}
              link={page[`link${index}`]}
              type={page[`linkType${index}`]}
              mandatory={mandatory}
              completed={() => {
                let { buttonsCompleted } = this.state;
                let newButtonAmount = buttonsCompleted + 1;
                this.setState({ buttonsCompleted: newButtonAmount });
                if (newButtonAmount === mandatoryAmount) {
                  this.setState({ buttonContentCompleted: true });
                }
              }}
            />
          );
          index += 1;
        }
      });
      this.setState({ buttons });
    }

    if (mandatoryAmount === 0) {
      this.setState({ buttonContentCompleted: true });
    }
  }

  checkCompletion(pageIdOld, chapterId) {
    const { pageId } = this.props.match.params;
    const { chapters } = this.progress.progress.data[0];
    const { progress } = chapters.filter(
      chapter => chapter.chapter === chapterId
    )[0];
    this.setState({ completedBefore: progress > +pageId });
  }

  navigationButtonCopy(pageId, chapterId) {
    const { chapters } = this.progress.progress.data[0];
    const lastChapter = chapters.slice(-1)[0].chapter;
    const { pages } = this.state;

    if (+pageId === pages.length - 1) {
      // Return complete course completed copy for final chapter instead of chapter complete copy
      if (lastChapter === chapterId) return i18n.translate("general.complete");

      return i18n.translate("general.chapterComplete");
    }

    return i18n.translate("general.next");
  }

  init() {
    const { chapterId, pageId } = this.props.match.params;
    return new Promise(resolve => {
      DataHandler.matchAppId().then(id => {
        this.setState({ appId: id });

        this.page = +pageId + 1;

        APIHandler.getTrainingPages(chapterId).then(pages => {
          let fetchedPages = [];
          if (this.props.type === "retain") {
            pages.forEach(page => {
              if (page.type === undefined && page.pageType !== "Final-Page") {
                fetchedPages.push(page);
              }
            });
          } else {
            fetchedPages = pages;
            // NOTE: Remove when QuestionTrueFalse is added to backend
            // fetchedPages.unshift({
            //   type: 'QuestionTrueFalse',
            //   UPID: 'testing',
            //   date: new Date(Date.now()),
            //   layout: '',
            //   pageNumber: 0,
            //   question: 'True or False?',
            //   correctAnswerText: 'Correct!',
            //   incorrectAnswerText: 'Incorrect!',
            //   answersCorrect: ['true', 'false'],
            // });
          }
          this.setButtons(fetchedPages[+pageId]);
          this.setState({ pages: fetchedPages }, resolve());
        });
      });
    });
  }

  initProgress() {
    const { chapterId, pageId } = this.props.match.params;
    const { user } = this.state;
    return new Promise(resolve => {
      DataHandler.matchAppId().then(appId => {
        DataHandler.checkKey(
          storageKeys.progress,
          APIHandler.getUserProgress(appId, user)
        ).then(progress => {
          resolve({
            progress,
            chapter: chapterId,
            page: pageId,
            pageCount: this.state.pages.length
          });
        });
      });
    });
  }

  nextPage() {
    const { history, type } = this.props;
    const { pageId, chapterId } = this.props.match.params;
    const { user } = this.state;
    window.scrollTo(0, 0);

    this.setButtons(this.state.pages[+pageId + 1]);

    if (type === "training") {
      this.setState({ contentCompleted: false, completedBefore: null });
    }

    DataHandler.matchAppId().then(appId => {
      DataHandler.checkKey(
        storageKeys.progress,
        APIHandler.getUserProgress(appId, user)
      ).then(progress => {
        if (+pageId + 1 < this.state.pages.length && type === "training") {
          this.page = +pageId + 1;
        } else if (type === "training") {
          return this.progress
            .update()
            .then(() => history.push("/main/training"));
        }
        if (type === "training") {
          this.progress = new ProgressHandler({
            progress,
            chapter: chapterId,
            page: this.page,
            pageCount: this.state.pages.length
          });

          return this.progress.update().then(() => {
            const target = `${this.props.redirPath}${chapterId}/${
              this.progress.page
            }`;
            this.setState({ ready: true });
            if (this.props.match.url !== target) {
              history.push(target);
            }
          });
        }
        if (type === "retain") {
          if (+pageId + 1 < this.state.pages.length) {
            this.page = +pageId + 2;
            history.push(`${this.props.redirPath}${chapterId}/${+pageId + 1}`);
          } else history.push("/main/training");
        }
        return false;
      });
    });
  }

  prevPage() {
    const { history, type } = this.props;
    const { pageId, chapterId } = this.props.match.params;
    const { user } = this.state;
    window.scrollTo(0, 0);

    this.setButtons(this.state.pages[+pageId - 1]);
    this.setState({ completedBefore: null });

    DataHandler.matchAppId().then(appId => {
      DataHandler.checkKey(
        storageKeys.progress,
        APIHandler.getUserProgress(appId, user)
      ).then(progress => {
        if (+pageId > 0 && type === "training") {
          this.page = +pageId - 1;

          this.progress = new ProgressHandler({
            progress,
            chapter: chapterId,
            page: this.page,
            pageCount: this.state.pages.length
          });

          this.progress.update().then(() => {
            const target = `${this.props.redirPath}${chapterId}/${
              this.progress.page
            }`;
            if (this.props.match.url !== target) {
              history.push(target);
            }
          });

          this.progress.update().then(() => {
            const target = `${this.props.redirPath}${chapterId}/${
              this.progress.page
            }`;
            if (this.props.match.url !== target) {
              history.push(target);
            }
          });
        } else history.push(`/main/${type}`);

        this.setState({ ready: true });

        if (type === "retain") {
          if (+pageId > 0) {
            history.push(`${this.props.redirPath}${chapterId}/${+pageId - 1}`);
          } else history.push("/main/retain");
        }

        return false;
      });
    });
  }

  renderContent() {
    const { pageId } = this.props.match.params;
    const { type } = this.props;
    const { appId, pages } = this.state;
    return (
      <TrainingContent
        type={type}
        appId={appId}
        params={pages[pageId]}
        pageId={pageId}
        LinkButtons={this.state.buttons}
        contentComplete={() => {
          this.setState({ contentCompleted: true, errors: 0 });
        }}
        error={increment => {
          if (increment) {
            const { errors } = this.state;
            this.setState({ errors: errors + 1 });
          }
          return (
            <SweetAlert
              html
              onCancel={() => {
                this.setState({ errors: 0 });
              }}
              onConfirm={() => {
                this.setState({ errors: 0 });
                this.nextPage();
              }}
              show={this.state.errors >= 7}
              cancelButtonText={i18n.translate("trainingScreens.retry")}
              confirmButtonText={i18n.translate("trainingScreens.skip")}
              showCancelButton
              title={i18n.translate("trainingScreens.trouble")}
            />
          );
        }}
      />
    );
  }

  render() {
    const { pageId, chapterId } = this.props.match.params;
    const {
      pages,
      contentCompleted,
      completedBefore,
      buttonContentCompleted,
      ready
    } = this.state;

    const contentIncomplete = !contentCompleted || !buttonContentCompleted;

    return (
      <PoseGroup flipMove={false}>
        {ready && [
          this.progress.pageIsAccessible().valid && (
            <div className="progress__container" key="progress__container">
              <Progress
                id="training_progress"
                value={Math.round((+pageId / (pages.length - 1)) * 100)}
              />
            </div>
          ),
          <AnimationContainer className="training" key={pageId}>
            <div className="trainingBackBtn">
              <Link to="/main/training" className="backBtn">
                <FontAwesomeIcon icon={['fas', 'times']} />
                {/* <p>{i18n.translate('general.back')}</p> */}
              </Link>
            </div>
            <style>{'\
              #openWidget { display: block; bottom: 4rem; } '}
            </style>
            <section className="trainingContent" key="trainingContent">
              {ready && this.progress.pageIsAccessible().valid && (
                <div>
                  <Breadcrumbs
                    mappedRoutes={this.state.crumbRoutes}
                    WrapperComponent={props => (
                      <ul className="breadcrumb__List">{props.children}</ul>
                    )}
                    LinkComponent={props => (
                      <li className="breadcrumb__item">
                        {props.children}
                        <span className="breadcrumb__separator">&#62;</span>
                      </li>
                    )}
                    ActiveLinkComponent={props => (
                      <li className="breadcrumb__item--active">
                        {props.children}
                      </li>
                    )}
                  />
                  <div id="parent" className="trainingContent__content">
                    {this.renderContent()}
                  </div>
                  <div className="trainingContent__buttons">
                    <NavigationButton
                      button={{
                        navigate: i18n.translate(
                          `general.${+pageId === 0 ? "overview" : "previous"}`
                        ),
                        disabled: false,
                        onClick: () => {
                          this.prevPage();
                        },
                        type: "previous"
                      }}
                    />
                    <div className="trainingContent__pageNumber">
                      <p>
                        {+pageId + 1} / {pages.length}
                      </p>
                    </div>
                    <NavigationButton
                      button={{
                        navigate: this.navigationButtonCopy(pageId, chapterId),
                        disabled:
                          (!completedBefore && contentIncomplete) ||
                          (contentIncomplete && !completedBefore),
                        onClick: () => {
                          this.nextPage();
                        },
                        type: "next",
                        id: "nextButton"
                      }}
                    />
                  </div>
                </div>
              )}
              {this.progress !== undefined &&
                (!this.progress.verifyPage() ||
                  !this.progress.pageIsAccessible().valid) &&
                this.props.match.url !==
                  this.progress.pageIsAccessible().target &&
                ready && (
                  <Redirect push to={this.progress.pageIsAccessible().target} />
                )}
            </section>
          </AnimationContainer>
        ]}
      </PoseGroup>
    );
  }
}

export default withRouter(TrainingContentView);

TrainingContentView.propTypes = {
  history: PropTypes.shape().isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape().isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  redirPath: PropTypes.string,
  type: PropTypes.string
};

TrainingContentView.defaultProps = {
  redirPath: "/main/training/chapter/",
  type: "training"
};
